// Project store — registry of all OPFS-resident projects + which one is
// currently "active" (i.e. the focus of the chat panel + canvas).
//
// Storage model:
//   - Each project is one top-level OPFS directory; the dir name IS the
//     project name (after slug normalization). No nested namespacing yet.
//   - The PROJECTS_INDEX_KEY entry in IDB stores an ordered list of
//     ProjectMeta records (name, createdAt, lastOpenedAt). This index
//     lets us show recents without walking OPFS on every cold boot.
//   - ACTIVE_PROJECT_KEY in IDB stores the name of the last-active
//     project so the next launch reopens it (same pattern as console's
//     last-project memory).
//
// We treat OPFS as the source of truth for "does this project exist" but
// rely on the IDB index for order + metadata. If they ever diverge (e.g.
// user clears IDB but keeps OPFS, or vice versa), `reconcileFromOpfs`
// lets us rebuild the index from a directory walk.
//
// Why a separate file from settings.ts:
//   - Different lifecycles. Settings change rarely; projects mutate every
//     time the user creates / opens / renames one.
//   - Different schemas. Mixing them would force a single schema-version
//     bump for either change.
//   - Easier mental model — one store per concern.

import { create } from 'zustand';

import { defaultFileStore } from '../core/filestore';
import { idbGet, idbSet } from '../lib/idb';
import { slugifyProjectName, uniqueProjectSlug } from '../lib/slug';

const PROJECTS_INDEX_KEY = 'projects-index';
const ACTIVE_PROJECT_KEY = 'active-project';
const SCHEMA_VERSION = 1;

export interface ProjectMeta {
  /** Folder name in OPFS root. Also serves as display name. */
  name: string;
  /** ISO-8601 timestamp the project was first created. */
  createdAt: string;
  /** ISO-8601 timestamp the project was last opened (for recents ordering). */
  lastOpenedAt: string;
}

interface ProjectsIndex {
  schemaVersion: typeof SCHEMA_VERSION;
  projects: ProjectMeta[];
}

const EMPTY_INDEX: ProjectsIndex = {
  schemaVersion: SCHEMA_VERSION,
  projects: [],
};

interface ProjectsStore {
  projects: ProjectMeta[];
  activeProjectName: string | null;
  hydrating: boolean;

  /** Pull index + active marker from IDB. Called once on app boot. */
  hydrate: () => Promise<void>;

  /**
   * Create a new project. `desiredName` is run through slug normalization
   * + uniqueness check. The OPFS directory is created. Returns the final
   * (possibly de-duplicated) project name.
   */
  createProject: (desiredName: string) => Promise<string>;

  /** Switch the active project. Bumps lastOpenedAt. */
  setActive: (name: string) => Promise<void>;

  /**
   * Rename a project. OPFS dir is renamed (copy + delete fallback); index
   * record is updated. If oldName is currently active, activeProjectName
   * is updated as well.
   */
  renameProject: (oldName: string, desiredNewName: string) => Promise<string>;

  /**
   * Delete a project entirely — OPFS dir + index record. If the deleted
   * project is currently active, activeProjectName resets to null.
   */
  deleteProject: (name: string) => Promise<void>;

  /**
   * Walk OPFS root, sync the index with whatever directories actually
   * exist. Useful when the user clears IDB or imports projects via a
   * future ZIP-import feature.
   */
  reconcileFromOpfs: () => Promise<void>;
}

export const useProjects = create<ProjectsStore>((set, get) => ({
  projects: [],
  activeProjectName: null,
  hydrating: true,

  hydrate: async () => {
    set({ hydrating: true });
    const stored = await idbGet<ProjectsIndex>(PROJECTS_INDEX_KEY);
    const active = await idbGet<string>(ACTIVE_PROJECT_KEY);
    const index =
      stored && stored.schemaVersion === SCHEMA_VERSION ? stored : EMPTY_INDEX;
    set({
      projects: index.projects,
      activeProjectName:
        active && index.projects.some((p) => p.name === active) ? active : null,
      hydrating: false,
    });
  },

  createProject: async (desiredName) => {
    const taken = get().projects.map((p) => p.name);
    const finalName = uniqueProjectSlug(desiredName, taken);
    const now = new Date().toISOString();
    // Create the OPFS folder eagerly so the agent's first tool call doesn't
    // fail with "directory missing" on a brand-new project.
    await defaultFileStore.ensureDir(finalName);
    const meta: ProjectMeta = {
      name: finalName,
      createdAt: now,
      lastOpenedAt: now,
    };
    const next = [meta, ...get().projects];
    await persistIndex(next);
    await idbSet(ACTIVE_PROJECT_KEY, finalName);
    set({ projects: next, activeProjectName: finalName });
    return finalName;
  },

  setActive: async (name) => {
    const list = get().projects;
    if (!list.some((p) => p.name === name)) {
      throw new Error(`setActive: project '${name}' not in index`);
    }
    const now = new Date().toISOString();
    const next = list.map((p) =>
      p.name === name ? { ...p, lastOpenedAt: now } : p,
    );
    // Sort recents-first by lastOpenedAt so the picker reflects use order.
    next.sort((a, b) => b.lastOpenedAt.localeCompare(a.lastOpenedAt));
    await persistIndex(next);
    await idbSet(ACTIVE_PROJECT_KEY, name);
    set({ projects: next, activeProjectName: name });
  },

  renameProject: async (oldName, desiredNewName) => {
    const list = get().projects;
    const exists = list.some((p) => p.name === oldName);
    if (!exists) throw new Error(`renameProject: source '${oldName}' missing`);
    const taken = list.filter((p) => p.name !== oldName).map((p) => p.name);
    const finalName = uniqueProjectSlug(desiredNewName, taken);
    if (finalName === oldName) return oldName;
    await defaultFileStore.rename(oldName, finalName);
    const next = list.map((p) =>
      p.name === oldName ? { ...p, name: finalName } : p,
    );
    await persistIndex(next);
    const wasActive = get().activeProjectName === oldName;
    if (wasActive) await idbSet(ACTIVE_PROJECT_KEY, finalName);
    set({
      projects: next,
      activeProjectName: wasActive ? finalName : get().activeProjectName,
    });
    return finalName;
  },

  deleteProject: async (name) => {
    const list = get().projects;
    if (!list.some((p) => p.name === name)) return;
    // Best-effort OPFS removal. If the directory is missing for any
    // reason (corrupted index, manual cleanup elsewhere) we still remove
    // it from the index so the UI doesn't keep showing a phantom.
    try {
      await defaultFileStore.deleteDir(name);
    } catch {
      // swallow — index cleanup proceeds regardless
    }
    const next = list.filter((p) => p.name !== name);
    await persistIndex(next);
    let activeNext = get().activeProjectName;
    if (activeNext === name) {
      activeNext = null;
      await idbSet(ACTIVE_PROJECT_KEY, '');
    }
    set({ projects: next, activeProjectName: activeNext });
  },

  reconcileFromOpfs: async () => {
    const indexed = new Map(get().projects.map((p) => [p.name, p]));
    const liveNames = new Set<string>();
    try {
      const entries = await defaultFileStore.listDir('');
      for (const e of entries) {
        if (e.kind === 'directory') liveNames.add(e.name);
      }
    } catch {
      // OPFS unavailable — leave index unchanged.
      return;
    }
    const now = new Date().toISOString();
    const merged: ProjectMeta[] = [];
    // Keep existing records for live folders.
    for (const name of liveNames) {
      const existing = indexed.get(name);
      merged.push(
        existing ?? { name, createdAt: now, lastOpenedAt: now },
      );
    }
    // Drop indexed records whose folder no longer exists.
    merged.sort((a, b) => b.lastOpenedAt.localeCompare(a.lastOpenedAt));
    await persistIndex(merged);
    let activeNext = get().activeProjectName;
    if (activeNext && !liveNames.has(activeNext)) {
      activeNext = null;
      await idbSet(ACTIVE_PROJECT_KEY, '');
    }
    set({ projects: merged, activeProjectName: activeNext });
  },
}));

async function persistIndex(projects: ProjectMeta[]): Promise<void> {
  const payload: ProjectsIndex = { schemaVersion: SCHEMA_VERSION, projects };
  await idbSet(PROJECTS_INDEX_KEY, payload);
}

// Re-export the slug helper for convenience — UI components that show a
// preview slug while the user types benefit from importing both store
// and helper from the same module.
export { slugifyProjectName };
